import {useState, type CSSProperties, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {
  CheckCircle2,
  ChevronLeft,
  Clock,
  Key,
  Lock,
  MapPin,
  QrCode,
  ShieldCheck,
} from 'lucide-react';
import {ChekinLogo, CtaDock, cn, type ChekinLogoProps} from '@chekinapp/ui';
import {LocaleButton} from 'components/LanguageButton';
import {useBrandingTheme} from 'context/brandingTheme';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useFirstScreenReady} from 'utils/firstScreen';
import {ArrivalPropertyProvider, PropertyScene} from '@chekin/shared/guest-form';
import {ArrivalSkeleton} from './ArrivalSkeleton';
import {useArrivalProperty} from './useArrivalProperty';
import styles from '@chekin/shared/guest-form/arrival.module.css';

type SealTone = 'official' | 'confirmed' | 'link' | 'scanned';

type Props = {
  children: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  navTitle?: string;
  seal?: SealTone | false;
  centered?: boolean;
  dock?: ReactNode;
  lead?: ReactNode;
  note?: ReactNode;
  unboxed?: boolean;
  stage?: boolean;
  wide?: boolean;
  perks?: boolean;
  heroTitle?: string;
  heroSubtitle?: ReactNode;
  className?: string;
};

const SEAL_TONE: Record<SealTone, {key: string; Icon: typeof ShieldCheck}> = {
  official: {key: 'arrival_seal_official_check_in', Icon: ShieldCheck},
  confirmed: {key: 'arrival_seal_booking_confirmed', Icon: CheckCircle2},
  link: {key: 'arrival_seal_property_link', Icon: ShieldCheck},
  scanned: {key: 'arrival_seal_scanned_at_this_door', Icon: QrCode},
};

function ArrivalShell({
  children,
  onBack,
  backLabel,
  navTitle,
  seal = 'official',
  centered,
  dock,
  lead,
  note,
  unboxed,
  stage,
  wide,
  perks: showPerks = true,
  heroTitle,
  heroSubtitle,
  className,
}: Props) {
  const {t} = useTranslation();
  const layoutMode = useLayoutMode();
  const property = useArrivalProperty();
  const isKiosko = layoutMode === 'kiosko';
  const [isScrolled, setIsScrolled] = useState(false);
  useFirstScreenReady();

  const heroHeight = isKiosko ? 320 : onBack ? 210 : 268;

  const sealTone = seal ? SEAL_TONE[seal] : undefined;

  if (property.isLoading) {
    return <ArrivalSkeleton wide={wide} className={className} />;
  }

  const identity = (
    <div className="flex flex-col gap-[7px]">
      {sealTone && (
        <span className="inline-flex h-[25px] w-fit items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--chekin-success)_50%,transparent)] bg-[color-mix(in_srgb,var(--chekin-success)_22%,transparent)] px-2.5 text-[12px] font-bold uppercase tracking-[0.03em] text-[color:var(--chekin-color-white)]">
          <sealTone.Icon size={13} className="text-[var(--chekin-green-500)]" />
          {t(sealTone.key)}
        </span>
      )}
      <h1 className="text-[27px] font-bold leading-[1.12] tracking-[-0.025em] text-white [text-shadow:0_2px_18px_rgba(21,21,63,0.45)] desk:text-[34px]">
        {heroTitle ?? property.name}
      </h1>
      {(heroSubtitle || property.location) && (
        <div className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-white/85">
          {heroSubtitle ?? (
            <>
              <MapPin size={14} />
              {[property.address, property.location].filter(Boolean).join(', ')}
            </>
          )}
        </div>
      )}
    </div>
  );

  const perks = [
    {Icon: Clock, label: t('arrival_perk_check_in_online')},
    {Icon: CheckCircle2, label: t('arrival_perk_no_paperwork')},
    {Icon: Key, label: t('arrival_perk_keys_ready')},
  ];

  const badges = [
    {Icon: Lock, label: t('arrival_badge_secured_by_chekin')},
    {Icon: ShieldCheck, label: t('arrival_badge_gdpr')},
    ...(property.isPoliceRegistered
      ? [{Icon: CheckCircle2, label: t('arrival_badge_officially_registered')}]
      : []),
  ];

  return (
    <ArrivalPropertyProvider value={property}>
      <div
        style={{'--arrival-hero-h': `${heroHeight}px`} as CSSProperties}
        className={cn(
          'relative flex h-[var(--app-dvh,100dvh)] w-full flex-col overflow-hidden bg-[var(--background-main)] desk:flex-row',
          className,
        )}
      >
        <aside className="relative hidden w-[38%] min-w-[380px] shrink-0 flex-col overflow-hidden [isolation:isolate] desk:flex">
          <PropertyScene narrow />
          <div className="relative z-[2] flex flex-1 flex-col px-[34px] py-[30px]">
            <BrandedLogo size="lg" />
            <div className="mt-auto flex flex-col gap-5 pb-1">
              {identity}
              {showPerks && (
                <div className="flex flex-col gap-[13px]">
                  {perks.map(({Icon, label}) => (
                    <div
                      key={label}
                      className="flex items-center gap-3 text-[13.5px] font-medium text-white"
                    >
                      <span className="grid size-8 shrink-0 place-items-center rounded-[10px] border border-white/20 bg-white/[0.14]">
                        <Icon size={16} />
                      </span>
                      {label}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {badges.map(({Icon, label}) => (
                  <span
                    key={label}
                    className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.12] px-2.5 text-[10.5px] font-semibold text-white"
                  >
                    <Icon size={13} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {!stage && (
          <div
            className={cn(
              'absolute inset-x-0 top-0 z-0 flex h-[var(--arrival-hero-h)] flex-col overflow-hidden desk:hidden',
              onBack ? 'min-h-[210px]' : 'min-h-[268px]',
              isKiosko && 'min-h-[320px]',
            )}
          >
            <PropertyScene />

            {!onBack && (
              <div className="relative z-[2] mt-auto px-[18px] pb-11">{identity}</div>
            )}
          </div>
        )}

        <div
          className={cn(
            styles.panel,
            'relative z-[3] flex min-h-0 min-w-0 flex-1 overflow-hidden',
          )}
        >
          {!stage && (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 top-0 z-[4] flex items-center justify-between gap-2.5 desk:hidden',
                'px-4 pb-3 pt-[calc(14px+env(safe-area-inset-top))] transition-colors duration-200',
                isScrolled && 'bg-[rgba(21,21,63,0.68)] backdrop-blur-[18px]',
              )}
            >
              {onBack ? (
                <div className="flex min-w-0 items-center gap-2.5">
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label={t('back')}
                    className="pointer-events-auto grid size-[34px] shrink-0 place-items-center rounded-[11px] border border-white/[0.26] bg-white/[0.16] text-white backdrop-blur-[14px] hover:bg-white/[0.28]"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="min-w-0">
                    {navTitle && (
                      <div className="truncate text-sm font-bold leading-[1.2] text-white">
                        {navTitle}
                      </div>
                    )}
                    <div className="truncate text-[11.5px] font-medium text-white/75">
                      {property.name}
                    </div>
                  </div>
                </div>
              ) : (
                <BrandedLogo />
              )}
              <LocaleButton
                variant="glass"
                display="code"
                className="pointer-events-auto h-[30px] shrink-0"
              />
            </div>
          )}

          <main
            onScroll={event => {
              const scrolled = event.currentTarget.scrollTop > 24;
              setIsScrolled(current => (current === scrolled ? current : scrolled));
            }}
            className={cn(
              'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-clip',
              'px-3.5 pb-0',
              stage
                ? 'pt-[calc(24px+env(safe-area-inset-top))]'
                : 'pt-[calc(var(--arrival-hero-h)-34px)]',
              'desk:items-center desk:p-[34px] desk:pt-[34px]',
            )}
          >
            <LocaleButton
              display="word"
              className="absolute right-[22px] top-[18px] z-[2] hidden desk:inline-flex"
            />

            <div
              className={cn(
                'relative z-[1] flex w-full flex-1 flex-col desk:flex-none',
                'mx-auto max-w-[520px] desk:my-auto',
                wide ? 'desk:max-w-[560px]' : 'desk:max-w-[450px]',
              )}
            >
              {lead && <div className="mb-3 w-full desk:hidden">{lead}</div>}

              <div
                className={cn(
                  'w-full',
                  unboxed || stage
                    ? cn(
                        !centered && 'flex flex-col gap-3 desk:block',
                        'desk:rounded-[24px] desk:bg-white',
                        'desk:px-[26px] desk:pb-[22px] desk:pt-[26px]',
                      )
                    : cn(
                        'rounded-[24px] bg-white',
                        'p-4 pt-[18px] desk:px-[26px] desk:pb-[22px] desk:pt-[26px]',
                      ),
                  centered &&
                    'flex flex-col items-center gap-4 text-center desk:pb-[26px] desk:pt-8',
                  stage && 'my-auto',
                )}
              >
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="mb-[34px] hidden items-center gap-1.5 self-start text-[13px] font-semibold text-[color:var(--chekin-text-secondary)] hover:text-[color:var(--chekin-color-brand-blue)] desk:inline-flex"
                  >
                    <ChevronLeft size={16} />
                    {backLabel ?? t('back')}
                  </button>
                )}
                {children}
                {note && (
                  <div className={cn('w-full text-left', !centered && 'mt-4')}>
                    {note}
                  </div>
                )}
                {dock && (
                  <div className="mt-6 hidden w-full flex-col gap-4 desk:flex">
                    {dock}
                  </div>
                )}
              </div>

              {!centered && (
                <div className="mt-4 flex items-center justify-center gap-1.5 text-[10.5px] font-semibold text-[color:var(--chekin-text-tertiary)] desk:hidden">
                  <Lock size={13} />
                  {t('arrival_trust_line')}
                </div>
              )}

              <p className="mt-5 hidden text-center text-[11px] font-semibold text-[color:var(--chekin-text-tertiary)] desk:block">
                {t('arrival_footer_copyright')}
              </p>

              {dock ? (
                <CtaDock
                  className="px-0 pt-7 desk:hidden"
                  style={
                    {
                      '--cta-dock-bg':
                        'linear-gradient(to top, var(--background-main) 68%, transparent)',
                    } as CSSProperties
                  }
                >
                  {dock}
                </CtaDock>
              ) : (
                <div className="h-6 desk:hidden" />
              )}
            </div>
          </main>
        </div>
      </div>
    </ArrivalPropertyProvider>
  );
}

function BrandedLogo({size}: {size?: ChekinLogoProps['size']}) {
  const {data: template} = useBrandingTheme();

  return (
    <ChekinLogo
      plate
      tone="onScene"
      size={size}
      src={template?.navigation_title_logo_download_link}
    />
  );
}

export {ArrivalShell};
